import React, { useState, useMemo } from 'react';
import { useThemeTokens } from '../../theme/ThemeProvider';

const DataTable = ({ 
  data = [],
  columns = [],
  sortable = true,
  paginated = true,
  pageSize = 10,
  searchable = false,
  selectable = false,
  onRowSelect,
  onRowClick,
  striped = true,
  compact = false,
  loading = false,
  emptyMessage = 'No hay datos para mostrar',
  className = '',
  style = {},
  ...props 
}) => {
  const { getColor, getSpacing, getFontSize, getBorderRadius, typography } = useThemeTokens();
  
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRows, setSelectedRows] = useState(new Set());
  
  // Procesar columnas automáticamente si no se proporcionan
  const processedColumns = useMemo(() => {
    if (columns.length > 0) return columns;
    
    if (data.length === 0) return [];
    
    return Object.keys(data[0]).map(key => ({
      key,
      title: key.charAt(0).toUpperCase() + key.slice(1),
      sortable: true,
      render: (value) => value,
    }));
  }, [data, columns]);
  
  // Filtrar datos por búsqueda
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    
    return data.filter(row =>
      processedColumns.some(col =>
        String(row[col.key] || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [data, searchTerm, processedColumns]);
  
  // Ordenar datos
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    
    return [...filteredData].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      
      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);
  
  // Paginar datos
  const paginatedData = useMemo(() => {
    if (!paginated) return sortedData;
    
    const startIndex = (currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, currentPage, pageSize, paginated]);
  
  const totalPages = Math.ceil(sortedData.length / pageSize);
  
  const handleSort = (key) => {
    if (!sortable) return;
    
    const column = processedColumns.find(col => col.key === key);
    if (column && !column.sortable) return;
    
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
  
  const handleRowSelect = (rowIndex, row) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(rowIndex)) {
      newSelected.delete(rowIndex);
    } else {
      newSelected.add(rowIndex);
    }
    setSelectedRows(newSelected);
    
    if (onRowSelect) {
      onRowSelect(Array.from(newSelected), row);
    }
  };
  
  const handleRowClick = (row, index) => {
    if (onRowClick) {
      onRowClick(row, index);
    }
  };
  
  const tableStyles = {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: getColor('neutral.white'),
    borderRadius: getBorderRadius('lg'),
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    ...style,
  };
  
  const headerStyles = {
    backgroundColor: getColor('neutral.lightGray'),
    borderBottom: `2px solid ${getColor('neutral.mediumGray')}`,
  };
  
  const headerCellStyles = {
    padding: compact ? getSpacing('sm') : getSpacing('md'),
    textAlign: 'left',
    fontSize: getFontSize('sm'),
    fontWeight: typography.fontWeights.semibold,
    color: getColor('neutral.darkGray'),
    cursor: sortable ? 'pointer' : 'default',
    userSelect: 'none',
    position: 'relative',
  };
  
  const cellStyles = {
    padding: compact ? getSpacing('sm') : getSpacing('md'),
    borderBottom: `1px solid ${getColor('neutral.lightGray')}`,
    fontSize: getFontSize('sm'),
    color: getColor('neutral.darkGray'),
  };
  
  const rowStyles = (index, isSelected) => ({
    backgroundColor: isSelected 
      ? getColor('primary.lightBlue') + '20'
      : striped && index % 2 === 1 
        ? getColor('neutral.lightGray') + '50' 
        : 'transparent',
    cursor: onRowClick ? 'pointer' : 'default',
    transition: 'background-color 0.15s ease',
  });
  
  const searchStyles = {
    width: '100%',
    maxWidth: '300px',
    padding: getSpacing('sm'),
    border: `1px solid ${getColor('neutral.mediumGray')}`,
    borderRadius: getBorderRadius('md'),
    fontSize: getFontSize('base'),
    marginBottom: getSpacing('md'),
  };
  
  const paginationStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: getSpacing('md'),
    padding: getSpacing('sm'),
    fontSize: getFontSize('sm'),
    color: getColor('neutral.mediumGray'),
  };
  
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: getSpacing('xl'),
        ...style
      }}>
        <span className="loading-spinner" />
        <span style={{ marginLeft: getSpacing('sm') }}>Cargando datos...</span>
      </div>
    );
  }
  
  if (data.length === 0) {
    return (
      <div style={{
        textAlign: 'center',
        padding: getSpacing('xl'),
        color: getColor('neutral.mediumGray'),
        backgroundColor: getColor('neutral.white'),
        borderRadius: getBorderRadius('lg'),
        border: `1px solid ${getColor('neutral.lightGray')}`,
        ...style
      }}>
        {emptyMessage}
      </div>
    );
  }
  
  return (
    <div className={`gaero-datatable ${className}`} {...props}>
      {searchable && (
        <div style={{ marginBottom: getSpacing('md') }}>
          <input
            type="text"
            placeholder="Buscar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={searchStyles}
          />
        </div>
      )}
      
      <div style={{ overflowX: 'auto' }}>
        <table style={tableStyles}>
          <thead style={headerStyles}>
            <tr>
              {selectable && (
                <th style={headerCellStyles}>
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedRows(new Set(data.map((_, i) => i)));
                      } else {
                        setSelectedRows(new Set());
                      }
                    }}
                    checked={selectedRows.size === data.length && data.length > 0}
                  />
                </th>
              )}
              {processedColumns.map((column) => (
                <th
                  key={column.key}
                  style={headerCellStyles}
                  onClick={() => handleSort(column.key)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: getSpacing('xs') }}>
                    {column.title}
                    {sortConfig.key === column.key && (
                      <span style={{ fontSize: '12px' }}>
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, index) => {
              const rowIndex = (currentPage - 1) * pageSize + index;
              const isSelected = selectedRows.has(rowIndex);
              
              return (
                <tr
                  key={rowIndex}
                  style={rowStyles(index, isSelected)}
                  onClick={() => handleRowClick(row, rowIndex)}
                >
                  {selectable && (
                    <td style={cellStyles}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleRowSelect(rowIndex, row)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                  )}
                  {processedColumns.map((column) => (
                    <td key={column.key} style={cellStyles}>
                      {column.render ? column.render(row[column.key], row, rowIndex) : row[column.key]}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {paginated && totalPages > 1 && (
        <div style={paginationStyles}>
          <div>
            Mostrando {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, sortedData.length)} de {sortedData.length} registros
          </div>
          <div style={{ display: 'flex', gap: getSpacing('sm'), alignItems: 'center' }}>
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              style={{
                padding: `${getSpacing('xs')} ${getSpacing('sm')}`,
                border: `1px solid ${getColor('neutral.mediumGray')}`,
                borderRadius: getBorderRadius('sm'),
                backgroundColor: getColor('neutral.white'),
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                opacity: currentPage === 1 ? 0.5 : 1,
              }}
            >
              Anterior
            </button>
            <span>
              Página {currentPage} de {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              style={{
                padding: `${getSpacing('xs')} ${getSpacing('sm')}`,
                border: `1px solid ${getColor('neutral.mediumGray')}`,
                borderRadius: getBorderRadius('sm'),
                backgroundColor: getColor('neutral.white'),
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                opacity: currentPage === totalPages ? 0.5 : 1,
              }}
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
